import { TranslationKey } from '@Localization';
import { inject, injectable, interfaces } from 'inversify';
import { action, makeObservable, observable } from 'mobx';
import { FileRejection } from 'react-dropzone/.';
import { ICurrentWorkspaceStore } from '../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { NotificationLevel, ToastNotification } from '../notifications/models/notification.model';
import { INotificationsService } from '../notifications/services/notifications.service';
import { IDatasetsDetailsStore } from './datasetsDetails.store';
import { isAllowedImageType, isAllowedSize, isNotDuplicatedExtension, toMBytes } from './helpers/image.helpers';
import { IImageFile, IImagesUploaderService } from './imagesUpload.service';
import { IRejectedFile } from './models/IRejectedFile';
import { RejectStatus } from './models/RejectStatus';

export interface IUpdateDatasetFilesService {
  setup: (datasetsStore: IDatasetsDetailsStore) => void;
  addImagesToDataset: (images: File[], fileRejections: FileRejection[]) => Promise<void>;
  cleanImagesFromFile: () => void;
  datasetsStore: IDatasetsDetailsStore;
  isProcessingImages: boolean;
}

export namespace IUpdateDatasetFilesService {
  export const $: interfaces.ServiceIdentifier<IUpdateDatasetFilesService> = Symbol('UPDATE_DATASET_FILES_SERVICE');
}

@injectable()
export class UpdateDatasetFilesService implements IUpdateDatasetFilesService {
  @observable
  datasetsStore!: IDatasetsDetailsStore;

  @observable
  isProcessingImages: boolean = false;

  imageProcessingCount: number = 0;
  acceptedImagesPlaceholder: IImageFile[] = [];
  rejectedImagesPlaceholder: IRejectedFile[] = [];

  constructor(
    @inject(INotificationsService.$) private readonly notificationsService: INotificationsService,
    @inject(IImagesUploaderService.$) private readonly imagesUploaderService: IImagesUploaderService,
    @inject(ICurrentWorkspaceStore.$) private readonly currentWorkspaceStore: ICurrentWorkspaceStore
  ) {
    makeObservable(this);
  }

  setup = (datasetsStore: IDatasetsDetailsStore): void => {
    this.datasetsStore = datasetsStore;
  };

  @action
  addImagesToDataset = async (images: File[], fileRejections: FileRejection[]): Promise<void> => {
    this.startProcessingImagesFiles();
    const acceptedImages: IImageFile[] = [];
    const rejectedImages: IRejectedFile[] = [];

    const duplicateNames = await this.imagesUploaderService.getDuplicatedImageNamesAsync(
      this.datasetsStore.details.id,
      images.map((x) => x.name).concat(this.acceptedImagesPlaceholder.map((x) => x.file.name))
    );

    for (const rejection of fileRejections) {
      if (rejection.errors.some((e) => e.code === 'file-invalid-type')) {
        rejectedImages.push({ file: rejection.file, status: RejectStatus.TYPE });
      }
    }

    for (const file of images) {
      if (duplicateNames.includes(file.name)) {
        rejectedImages.push({ file, status: RejectStatus.DUPLICATED_NAME });
        continue;
      }
      if (!isNotDuplicatedExtension(file.name)) {
        rejectedImages.push({ file, status: RejectStatus.DUPLICATED_EXTENSION });
        continue;
      }
      if (!isAllowedImageType(file.name)) {
        rejectedImages.push({ file, status: RejectStatus.TYPE });
        continue;
      }
      if (!isAllowedSize(file)) {
        rejectedImages.push({ file, status: RejectStatus.SIZE });
        continue;
      }
      acceptedImages.push({ file });
    }
    await this.endProcessingImagesFiles(acceptedImages, rejectedImages);
  };

  @action
  handleFileParseError = async (error: Error): Promise<void> => {
    await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, error.message as TranslationKey, 'file_parse_error'));
  };

  @action
  cleanImagesFromFile = (): void => {
    this.datasetsStore.imagePreValidationWasSuccessful = true;
    this.datasetsStore.imagesToUploadFromFile.clear();
    this.datasetsStore.imagesRejectedFromFile.clear();
    this.imageProcessingCount = 0;
    this.acceptedImagesPlaceholder = [];
    this.rejectedImagesPlaceholder = [];
    this.isProcessingImages = false;
  };

  @action
  startProcessingImagesFiles = (): void => {
    this.imageProcessingCount += 1;
    this.isProcessingImages = this.imageProcessingCount > 0;
    if (this.imageProcessingCount <= 1) {
      this.acceptedImagesPlaceholder = [...this.datasetsStore.imagesToUploadFromFile];
      this.rejectedImagesPlaceholder = [...this.datasetsStore.imagesRejectedFromFile];
    }
  };

  @action
  endProcessingImagesFiles = async (acceptedImages: IImageFile[], rejectedImages: IRejectedFile[]): Promise<void> => {
    this.imageProcessingCount = this.imageProcessingCount > 0 ? this.imageProcessingCount - 1 : 0;
    this.acceptedImagesPlaceholder.push(...acceptedImages);
    this.rejectedImagesPlaceholder.push(...rejectedImages);
    if (this.imageProcessingCount <= 0) {
      this.isProcessingImages = false;
      this.datasetsStore.imagesToUploadFromFile.replace(this.acceptedImagesPlaceholder);
      this.datasetsStore.imagesRejectedFromFile.replace(this.rejectedImagesPlaceholder);
    }

    const willExceeds = await this.imagesUploaderService.willExceedsImageLimit(
      this.currentWorkspaceStore.currentWorkspace.id,
      this.datasetsStore.imagesToUploadFromFile.length,
      toMBytes(this.datasetsStore.imagesToUploadFromFile.reduce((sum, current) => sum + current.file.size, 0))
    );

    this.datasetsStore.willExceedsUploadImageSizeLimit = willExceeds.willExceedsAvailableSpace;
    this.datasetsStore.imagePreValidationWasSuccessful = willExceeds.areAllSuccessful;
    this.datasetsStore.ownerPlan = willExceeds.ownerPlan;
  };
}
